import { useContext } from "react";
import {
  Info,
  Accessibility,
  Volume2,
  Eye,
  Settings,
  Heart,
  ExternalLink,
} from "lucide-react";
import { FontScaleContext } from "@/App";
import { Slider } from "@/components/ui/slider";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

export default function InfoView() {
  const { fontScale, setFontScale } = useContext(FontScaleContext);

  const handleFontScaleChange = (value) => {
    setFontScale(value[0]);
  };

  const handleResetWelcome = () => {
    localStorage.removeItem("hasVisited");
    window.location.reload();
  };

  const features = [
    {
      icon: Volume2,
      title: "Sensory Warnings",
      description:
        "Get alerts before loud events like fireworks, sirens, and crowd chants.",
    },
    {
      icon: Eye,
      title: "Quiet Zone Finder",
      description:
        "Locate designated quiet spaces and sensory rooms with live capacity updates.",
    },
    {
      icon: Accessibility,
      title: "Accessibility Info",
      description:
        "Find accessible seating, companion seats, and ADA accommodation services.",
    },
  ];

  const unseeDisabilities = [
    { icon: "∞", label: "Neurodiversity Support", color: "bg-blue-600" },
    { icon: "🌊", label: "PTSD/Anxiety Zone", color: "bg-blue-400" },
    { icon: "🪑", label: "Chronic Illness Rest", color: "bg-green-500" },
    { icon: "🌻", label: "Sunflower Lanyard", color: "bg-yellow-500" },
  ];

  return (
    <div className="p-4 pb-safe" data-testid="info-view">
      {/* Header with Logo */}
      <header className="mb-6 flex items-center gap-4">
        <img
          src="https://customer-assets.emergentagent.com/job_8e534113-c2c4-4f3f-8864-185da76b04eb/artifacts/d11zdlcb_XK%20Inclusive%20GameDay%20Logo.png"
          alt="XTERMIGATOR Kids Logo"
          className="w-24 h-24 object-contain"
        />
        <div>
          <h1
            className="text-2xl font-bold text-foreground"
            style={{ fontFamily: "Lexend, sans-serif" }}
          >
            XTERMIGATOR Kids
          </h1>
          <p className="text-base text-muted-foreground">
            Inclusive Game Day
          </p>
        </div>
      </header>

      {/* Website Link - Prominent */}
      <section className="mb-6">
        <a
          href="https://www.inclusivegameday.com"
          target="_blank"
          rel="noopener noreferrer"
          className="block"
          data-testid="inclusivegameday-link-info"
        >
          <Card className="bg-gradient-to-r from-[#0021A5] to-[#FA4616] text-white border-0 hover:opacity-90 transition-opacity">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                  <ExternalLink size={28} />
                </div>
                <div>
                  <h3
                    className="font-bold text-lg"
                    style={{ fontFamily: "Lexend, sans-serif" }}
                  >
                    Visit Our Website
                  </h3>
                  <p className="text-sm text-white/90">
                    www.inclusivegameday.com
                  </p>
                  <p className="text-xs text-white/70 mt-1">
                    More resources, social stories & pre-visit guides
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </a>
      </section>

      {/* About Section */}
      <section className="mb-6" aria-label="About XTERMIGATOR Kids">
        <Card className="border-[#0021A5]/20">
          <CardHeader className="pb-3">
            <CardTitle
              className="text-lg flex items-center gap-2"
              style={{ fontFamily: "Lexend, sans-serif" }}
            >
              <Heart size={20} className="text-[#FA4616]" />
              About This Project
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground leading-relaxed mb-4">
              <strong>XTERMIGATOR Kids Inclusive Game Day</strong> is designed for fans with 
              sensory sensitivities, autism spectrum conditions, anxiety, PTSD, or other 
              unseen disabilities. Our goal is to help you enjoy the game-day experience 
              safely and comfortably.
            </p>
            <div className="bg-[#0021A5]/5 rounded-xl p-4 border border-[#0021A5]/10">
              <p className="text-sm text-center">
                <span className="text-muted-foreground">A Graduate Project for the</span>
                <br />
                <span className="font-semibold text-[#0021A5]">College of Education</span>
                <br />
                <span className="text-muted-foreground">by</span>
                <br />
                <span className="font-bold text-[#FA4616]">Dr. Eric S. Fishon</span>
                <br />
                <span className="text-xs text-muted-foreground">
                  Founder/Creator of XTERMIGATOR Kids
                </span>
              </p>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Unseen Disabilities Legend */}
      <section className="mb-6" aria-label="Unseen disabilities legend">
        <h2
          className="section-header text-lg"
          style={{ fontFamily: "Lexend, sans-serif" }}
        >
          Unseen Disabilities Legend
        </h2>
        
        <div className="grid grid-cols-2 gap-3">
          {unseeDisabilities.map((item) => (
            <div
              key={item.label}
              className="flex items-center gap-2 p-3 bg-white rounded-xl border shadow-sm"
            >
              <span className="text-lg">{item.icon}</span>
              <span className="text-xs font-medium">{item.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="mb-6" aria-label="App features">
        <h2
          className="section-header text-lg"
          style={{ fontFamily: "Lexend, sans-serif" }}
        >
          Features
        </h2>

        <div className="space-y-3">
          {features.map((feature) => {
            const Icon = feature.icon;
            return (
              <div
                key={feature.title}
                className="info-card flex items-start gap-4"
              >
                <div className="w-10 h-10 rounded-full bg-[#0021A5]/10 flex items-center justify-center flex-shrink-0">
                  <Icon size={20} className="text-[#0021A5]" />
                </div>
                <div>
                  <h3
                    className="font-semibold text-base"
                    style={{ fontFamily: "Lexend, sans-serif" }}
                  >
                    {feature.title}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {feature.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Settings */}
      <section className="mb-6" aria-label="Accessibility settings">
        <h2
          className="section-header text-lg flex items-center gap-2"
          style={{ fontFamily: "Lexend, sans-serif" }}
        >
          <Settings size={20} />
          Settings
        </h2>

        <Card>
          <CardContent className="pt-6">
            <div className="space-y-6">
              {/* Font Size */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <label
                    htmlFor="font-scale"
                    className="font-medium text-base"
                    style={{ fontFamily: "Lexend, sans-serif" }}
                  >
                    Text Size
                  </label>
                  <span className="text-sm text-muted-foreground">
                    {Math.round(fontScale * 100)}%
                  </span>
                </div>

                <Slider
                  id="font-scale"
                  min={0.8}
                  max={1.4}
                  step={0.1}
                  value={[fontScale]}
                  onValueChange={handleFontScaleChange}
                  className="w-full"
                  aria-label="Adjust text size"
                  data-testid="font-scale-slider"
                />

                <div className="flex justify-between text-xs text-muted-foreground mt-2">
                  <span>Smaller</span>
                  <span>Default</span>
                  <span>Larger</span>
                </div>
              </div>

              {/* Preview */}
              <div className="p-4 bg-secondary rounded-xl">
                <p className="text-sm mb-1 font-medium">Preview:</p>
                <p
                  className="text-muted-foreground"
                  style={{ fontSize: `${fontScale}rem` }}
                >
                  This is how your text will appear throughout the app.
                </p>
              </div>

              {/* Reset Welcome */}
              <Button
                variant="outline"
                className="w-full"
                onClick={handleResetWelcome}
                data-testid="reset-welcome"
              >
                Show Welcome Screen Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Venue Info */}
      <section aria-label="Venue information">
        <h2
          className="section-header text-lg"
          style={{ fontFamily: "Lexend, sans-serif" }}
        >
          Current Venue
        </h2>

        <Card className="border-[#FA4616]/20">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[#0021A5] to-[#FA4616] flex items-center justify-center flex-shrink-0">
                <span
                  className="text-2xl font-bold text-white"
                  style={{ fontFamily: "Lexend, sans-serif" }}
                >
                  UF
                </span>
              </div>
              <div>
                <h3
                  className="font-semibold text-base"
                  style={{ fontFamily: "Lexend, sans-serif" }}
                >
                  University of Florida
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Ben Hill Griffin Stadium
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  "The Swamp" - Gainesville, FL
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Footer */}
      <footer className="mt-8 text-center">
        <img
          src="https://customer-assets.emergentagent.com/job_8e534113-c2c4-4f3f-8864-185da76b04eb/artifacts/d11zdlcb_XK%20Inclusive%20GameDay%20Logo.png"
          alt="XTERMIGATOR Kids Logo"
          className="w-16 h-16 mx-auto mb-2 opacity-50"
        />
        <p className="text-xs text-muted-foreground">
          XTERMIGATOR Kids Inclusive Game Day v1.0
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          Making game days accessible for everyone
        </p>
        <p className="text-xs text-[#FA4616] mt-2 font-medium">
          Go Gators! 🐊
        </p>
      </footer>
    </div>
  );
}
